import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Container,
  Nav,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

import Header_Manager from "../../Components/Header_Manager";
import Company_Agent from "../../Components/CompanyDetails/Company_Agent";
import Company_Supervisor from "../../Components/CompanyDetails/Company_Supervisor";
import { BASE_URL } from "../../Config";
import "./Manager.css";

const Manager_Team = () => {
  const [activeTab, setActiveTab] = useState("supervisor");
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const loadAssignedCompanies = async () => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (!user.userId) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }

    setLoadingCompanies(true);
    try {
      const response = await axios.get(
        `${BASE_URL}AssignedCompany?ManagerId=${user.userId}`
      );

      if (
        response.data.responseCode === "0000" &&
        Array.isArray(response.data.companyDetails)
      ) {
        const list = response.data.companyDetails;
        setCompanies(list);
        sessionStorage.setItem(
          "user",
          JSON.stringify({ ...user, assignedCompanies: list })
        );

        const preferred =
          user.selectedCompanyId ||
          user.companyId ||
          list[0]?.companyProfileId ||
          "";
        const exists = list.some((c) => c.companyProfileId === preferred);
        setSelectedCompanyId(exists ? preferred : list[0]?.companyProfileId || "");
      } else {
        setCompanies([]);
        setSelectedCompanyId("");
        Swal.fire(
          "Info",
          response.data.message || "No companies are currently assigned.",
          "info"
        );
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to fetch assigned companies.", "error");
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (Array.isArray(user.assignedCompanies) && user.assignedCompanies.length > 0) {
      setCompanies(user.assignedCompanies);
      setSelectedCompanyId(
        user.selectedCompanyId ||
          user.companyId ||
          user.assignedCompanies[0]?.companyProfileId ||
          ""
      );
    }
    loadAssignedCompanies();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const company = companies.find((c) => c.companyProfileId === selectedCompanyId);
    const user = JSON.parse(sessionStorage.getItem("user") || "{}");
    sessionStorage.setItem(
      "user",
      JSON.stringify({
        ...user,
        companyId: selectedCompanyId,
        selectedCompanyId,
        selectedCompanyName: company?.name || user.selectedCompanyName || "",
        companyName: company?.name || user.companyName || "",
      })
    );
  }, [selectedCompanyId, companies]);

  const selectedCompanyName =
    companies.find((c) => c.companyProfileId === selectedCompanyId)?.name || "";

  return (
    <div className="mgr-page">
      <Header_Manager />

      <Container fluid="xl" className="px-3 px-md-4">
        <div className="mgr-hero">
          <h4 className="fw-semibold">
            <i className="bi bi-people me-2" />
            Company Team
          </h4>
          <p>
            View supervisors and agents for an assigned company, and reset their passwords.
          </p>
        </div>

        <Card className="mgr-card mb-3">
          <Card.Body className="py-3">
            <Row className="g-2 align-items-end">
              <Col xs={12} md={8}>
                <Form.Label className="fw-semibold mb-1">Select Company</Form.Label>
                <Form.Select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  disabled={loadingCompanies}
                  size="lg"
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.companyProfileId} value={c.companyProfileId}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={4}>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={loadAssignedCompanies}
                  disabled={loadingCompanies}
                >
                  {loadingCompanies ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" /> Loading
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1" /> Refresh
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {selectedCompanyId && selectedCompanyName && (
              <Alert variant="primary" className="mt-3 mb-0 py-2">
                Selected: <strong>{selectedCompanyName}</strong>
              </Alert>
            )}
          </Card.Body>
        </Card>

        {!selectedCompanyId ? (
          <Alert variant="warning" className="mb-0">
            Please select a company to view its supervisors and agents.
          </Alert>
        ) : (
          <>
            <Nav variant="pills" className="mgr-step-nav gap-2 mb-3 flex-nowrap">
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "supervisor"}
                  onClick={() => setActiveTab("supervisor")}
                >
                  <i className="bi bi-person-badge me-1" />
                  Supervisors
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  active={activeTab === "agent"}
                  onClick={() => setActiveTab("agent")}
                >
                  <i className="bi bi-person-vcard me-1" />
                  Agents
                </Nav.Link>
              </Nav.Item>
            </Nav>

            {activeTab === "supervisor" ? (
              <Company_Supervisor
                key={`sup-${selectedCompanyId}`}
                companyUserId={selectedCompanyId}
                companyName={selectedCompanyName}
                allowRegister={false}
              />
            ) : (
              <Company_Agent
                key={`agt-${selectedCompanyId}`}
                companyUserId={selectedCompanyId}
                companyName={selectedCompanyName}
                allowRegister={false}
              />
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default Manager_Team;
